import { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useThemeMode } from '../../context/ThemeContext';
import {
  AppBar,
  Box,
  Toolbar,
  Typography,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  Container,
  useMediaQuery,
  useTheme,
  Tooltip,
  Chip,
} from '@mui/material';
import {
  Menu as MenuIcon,
  FlightTakeoff as FlightIcon,
  Business as OficinaIcon,
  Assessment as ReportesIcon,
  Logout as LogoutIcon,
  DarkMode as DarkModeIcon,
  LightMode as LightModeIcon,
  Person as PersonIcon,
  Pallet as PalletIcon ,
  AdminPanelSettings as AdminIcon,
  Warehouse as WarehouseIcon,
  Airlines as AerolineaIcon,
  Receipt as FacturacionIcon,
  Badge as BadgeIcon,
} from '@mui/icons-material';

const drawerWidth = 280;

// Items del menú con permisos requeridos
const menuItems = [
  { 
    text: 'Vuelos', 
    icon: <FlightIcon />, 
    path: '/vuelos',
    permission: 'vuelos',
  },
  { 
    text: 'Warehouse', 
    icon: <WarehouseIcon />, 
    path: '/warehouse',
    permission: 'warehouse',
  },
  { 
    text: 'Aerolínea', 
    icon: <AerolineaIcon />, 
    path: '/aerolinea',
    permission: 'aerolinea',
  },
  { 
    text: 'Facturación', 
    icon: <FacturacionIcon />, 
    path: '/facturacion',
    permission: 'facturacion',
  },
  { 
    text: 'Oficina', 
    icon: <OficinaIcon />, 
    path: '/oficina',
    permission: 'oficina',
  },
  { 
    text: 'Reportes', 
    icon: <ReportesIcon />, 
    path: '/reportes',
    permission: 'reportes',
  },
  { 
    text: 'Administración', 
    icon: <AdminIcon />, 
    path: '/admin', 
    permission: 'admin',
  },
];

// Colores por rol
const getRoleColor = (role) => {
  switch (role) {
    case 'admin': return 'error';
    case 'supervisor': return 'warning';
    case 'booking': return 'info';
    case 'recepcion': return 'secondary';
    case 'paletizador': return 'primary';
    case 'facturacion': return 'success';
    default: return 'default';
  }
};

const Layout = ({ children }) => {
  const { user, logout, hasModuleAccess } = useAuth();
  const { toggleTheme, isDark } = useThemeMode();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    handleMenuClose();
    logout();
    navigate('/login');
  };

  const isActiveRoute = (path) => {
    return location.pathname.startsWith(path);
  };

  // Filtrar items del menú según permisos del usuario
  const visibleMenuItems = menuItems.filter((item) => {
    return hasModuleAccess(item.permission);
  });

  const drawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', bgcolor: 'background.paper' }}>
      {/* Logo */}
      <Box
        sx={{
          p: 3,
          background: 'linear-gradient(135deg, #1565C0 0%, #0D47A1 100%)',
          color: 'white',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <PalletIcon sx={{ fontSize: 40 }} />
          <Box>
            <Typography variant="h6" fontWeight="bold">
              PalletControl.us
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.8 }}>
              
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* User Info in Drawer */}
      {user && (
        <Box 
          sx={{ 
            p: 2, 
            bgcolor: isDark ? 'rgba(255,255,255,0.05)' : 'grey.100',
            borderBottom: 1,
            borderColor: 'divider',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Avatar sx={{ bgcolor: 'primary.main', width: 36, height: 36 }}>
              {user.nombre?.charAt(0) || 'U'}
            </Avatar>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="body2" fontWeight="bold" noWrap>
                {user.nombre}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
              
                <Chip
                  label={user.base_estacion}
                  size="small"
                  variant="outlined"
                  sx={{ height: 20, fontSize: '0.7rem' }}
                />
              </Box>
            </Box>
          </Box>
        </Box>
      )}

      {/* Navigation */}
      <List sx={{ flex: 1, py: 2, bgcolor: 'background.paper' }}>
        {visibleMenuItems.map((item) => (
          <ListItem key={item.text} disablePadding sx={{ px: 2, mb: 0.5 }}>
            <ListItemButton
              component={Link}
              to={item.path}
              onClick={() => isMobile && setMobileOpen(false)}
              sx={{
                borderRadius: 2,
                backgroundColor: isActiveRoute(item.path)
                  ? 'primary.main'
                  : 'transparent',
                color: isActiveRoute(item.path) ? 'white' : 'text.primary',
                '&:hover': {
                  backgroundColor: isActiveRoute(item.path)
                    ? 'primary.dark'
                    : 'action.hover',
                },
              }}
            >
              <ListItemIcon
                sx={{
                  color: isActiveRoute(item.path) ? 'white' : 'primary.main',
                  minWidth: 44,
                }}
              >
                {item.icon}
              </ListItemIcon>
              <ListItemText
                primary={item.text}
                primaryTypographyProps={{ fontWeight: 500 }}
              />
            </ListItemButton>
          </ListItem>
        ))}
      </List>

      {/* Footer del drawer */}
      <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider', bgcolor: 'background.paper' }}>
        <Typography variant="caption" color="text.secondary" display="block" textAlign="center">
          © 2025 Pallet Control
        </Typography>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {/* AppBar */}
      <AppBar
        position="fixed"
        sx={{
          width: { md: `calc(100% - ${drawerWidth}px)` },
          ml: { md: `${drawerWidth}px` },
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { md: 'none' } }}
          >
            <MenuIcon />
          </IconButton>

          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            {menuItems.find((item) => isActiveRoute(item.path))?.text || 'Dashboard'}
          </Typography>


          {/* Theme Toggle */}
          <Tooltip title={isDark ? 'Modo claro' : 'Modo oscuro'}>
            <IconButton color="inherit" onClick={toggleTheme} sx={{ mr: 1 }}>
              {isDark ? <LightModeIcon /> : <DarkModeIcon />}
            </IconButton>
          </Tooltip>

          {/* User Menu */}
          {user && (
            <>
              <Chip
                avatar={
                  <Avatar sx={{ bgcolor: 'primary.dark' }}>
                    {user.nombre?.charAt(0) || 'U'}
                  </Avatar>
                }
                label={
                  <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
                    <Typography variant="body2" fontWeight="medium">
                      {user.nombre}
                    </Typography>
                  </Box>
                }
                onClick={handleMenuOpen}
                sx={{
                  height: 'auto',
                  py: 0.5,
                  px: 1,
                  backgroundColor: 'rgba(255,255,255,0.15)',
                  color: 'white',
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: 'rgba(255,255,255,0.25)',
                  },
                }}
              />
              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
              >
                <Box sx={{ px: 2, py: 1 }}>
                  <Typography variant="subtitle1" fontWeight="bold">
                    {user.nombre}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {user.cargo}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 0.5, mt: 1 }}>
                 
                    <Chip
                      label={user.base_estacion}
                      size="small"
                      variant="outlined"
                    />
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    {user.email}
                  </Typography>
                </Box>
                <Divider />
                <MenuItem onClick={handleMenuClose}>
                  <ListItemIcon>
                    <PersonIcon fontSize="small" />
                  </ListItemIcon>
                  Mi Perfil
                </MenuItem>
                <MenuItem onClick={handleLogout} sx={{ color: 'error.main' }}>
                  <ListItemIcon>
                    <LogoutIcon fontSize="small" color="error" />
                  </ListItemIcon>
                  Cerrar Sesión
                </MenuItem>
              </Menu>
            </>
          )}
        </Toolbar>
      </AppBar>

      {/* Drawer - Mobile */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': { 
            boxSizing: 'border-box', 
            width: drawerWidth,
            bgcolor: 'background.paper',
          },
        }}
      >
        {drawer}
      </Drawer>

      {/* Drawer - Desktop */}
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: 'none', md: 'block' },
          width: drawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': { 
            boxSizing: 'border-box', 
            width: drawerWidth,
            bgcolor: 'background.paper',
            borderRight: 1,
            borderColor: 'divider',
          },
        }}
        open
      >
        {drawer}
      </Drawer>

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: { md: `calc(100% - ${drawerWidth}px)` },
          minHeight: '100vh',
          backgroundColor: 'background.default',
        }}
      >
        <Toolbar /> {/* Spacer for AppBar */}
        <Container maxWidth="xl" sx={{ py: 3 }}>
          {children}
        </Container>
      </Box>
    </Box>
  );
};

export default Layout;
